/**
 * Meddelande om ändring av hyresvillkor — tilläggsavtal (12 kap. 54 § JB).
 *
 * Two parts, as in the legacy template (ADR-0057): the meddelande (hyresvärd
 * signs) and the tillägg (hyresgäst[er] + hyresvärdens representant sign).
 * § numbering restarts per part, as in the legacy template: the meddelande
 * prints § 1–2 and the tillägg its own § 1–2.
 */
import { COMPOSITION_SCHEMA, COMPOSITION_VERSION } from '@hyranai/regta-document-ir';
const TITLE = 'Meddelande om ändring – tilläggsavtal';
const FORM_NUMBER = 'Formulär R-11';
export const ANDRING_PARTY_SLOTS = [
    {
        id: 'lessor',
        label: 'Hyresvärd',
        signingLabel: 'Hyresvärden (firmatecknare/ombud)',
        role: 'lessor',
        ordinal: 0,
        required: true,
        signs: true,
    },
    {
        id: 'lessee_primary',
        label: 'Hyresgäst (primär)',
        signingLabel: 'Hyresgästen',
        role: 'lessee',
        ordinal: 0,
        required: true,
        signs: true,
    },
    {
        id: 'lessee_co',
        label: 'Medhyresgäst',
        signingLabel: 'Ev. hyresgäst 2',
        role: 'lessee',
        ordinal: 1,
        signs: true,
        help: 'Valfritt — lägg till om kontraktet har två hyresgäster.',
    },
];
/** The meddelande is signed by the hyresvärd alone. */
export const MEDDELANDE_SIGNERS = [{ label: 'Hyresvärden (firmatecknare/ombud)', role: 'lessor', ordinal: 0 }];
/** The tillägg: hyresgäst(er), then the hyresvärd's representative. */
export const TILLAGG_SIGNERS = [
    { label: 'Hyresgästen', role: 'lessee', ordinal: 0 },
    { label: 'Ev. hyresgäst 2', role: 'lessee', ordinal: 1, when_set: 'cotenant_name' },
    { label: 'Hyresvärdens representant', role: 'lessor', ordinal: 0 },
];
/**
 * Legacy `TemplateMetadata` host fields shared by both ändring variants:
 * a connected child of a bostadshyra (ADR-0055) that inherits parties,
 * property, the apartment number and the contract number at creation.
 */
export const ANDRING_HOST_META = {
    status: 'active',
    titleSuffix: { key: 'contract_number', prefix: 'avtal' },
    connectedTo: ['bostadshyra'],
    inheritFromParent: {
        parties: 'by-role-ordinal',
        property: true,
        values: [
            { key: 'apartment_official_number', fromParentKey: 'property_official_apt_no' },
            { key: 'contract_number', fromParentKey: 'contract_number' },
        ],
    },
    signingPlacementAnchors: 'per-party-label',
};
export const andringHyresvillkorV1 = (values = {}) => ({
    schema: COMPOSITION_SCHEMA,
    version: COMPOSITION_VERSION,
    documentKey: 'andring_hyresvillkor',
    documentVersion: 'v1',
    title: TITLE,
    locale: 'sv-SE',
    meta: {
        category: 'Ändring av hyresvillkor',
        numberStyle: 'paragraph',
        shortTitle: 'Ändring av hyresvillkor',
        formNumber: FORM_NUMBER,
        statute: '12 kap. 54 § jordabalken',
        signingMode: 'manual_delivery',
        ...ANDRING_HOST_META,
        preamble: 'Hyresvärden meddelar härmed hyresgästen om föreslagen ändring av hyresvillkoren, åtföljt av tillägg till hyresavtalet.',
        description: 'Enligt 12 kap. 54 § jordabalken.\n\n' +
            'Meddelande från hyresvärden om att denne vill ändra hyresvillkoren för en bostadslägenhet, åtföljt av ett tillägg till hyresavtalet som hyresgästen ska underteckna inom angiven svarsfrist.\n\n' +
            'Vem undertecknar: hyresvärden (meddelandet) och hyresgästen — samt eventuell medhyresgäst — (tillägget).\n' +
            'Leverans: skrivs ut och skickas per post (statutoriskt meddelande — signeras ej via Scrive).\n\n' +
            'Om hyresgästen inte skriver under tillägget inom svarsfristen får hyresvärden lämna in ärendet till hyresnämnden, dock tidigast en månad efter att meddelandet lämnats.\n\n' +
            'För det specifika fallet "införande av förhandlingsklausul" finns en separat mall (Ändring av hyresvillkor — införande av förhandlingsklausul) där lagtexten är förtryckt.\n\n' +
            'Kopplad mall — skapas under det bostadshyresavtal som ändringen avser. Lägenhetens uppgifter och parterna förförifylls från bostadsavtalet.',
        partySlots: ANDRING_PARTY_SLOTS,
        propertySlots: [{ id: 'property', label: 'Fastighet', multi: false, required: true }],
    },
    parts: [
        { key: 'meddelande', title: 'Meddelande om ändring av hyresvillkor' },
        { key: 'tillagg', title: 'Tillägg till hyresavtalet' },
    ],
    values,
    blocks: [
        {
            instanceId: 'letterhead-meddelande',
            blockRef: 'common.letterhead@v1',
            part: 'meddelande',
            slot: 'letterhead',
            input: { title: TITLE, form_number: FORM_NUMBER },
        },
        {
            instanceId: 'footer-meddelande',
            blockRef: 'common.page-footer@v1',
            part: 'meddelande',
            slot: 'footer',
            input: { form_number: FORM_NUMBER },
        },
        { instanceId: 'rubrik', blockRef: 'andring_hyresvillkor.rubrik@v1', part: 'meddelande', input: {} },
        { instanceId: 'uppgifter-meddelande', blockRef: 'andring_hyresvillkor.uppgifter@v1', part: 'meddelande', input: {} },
        { instanceId: 'meddelande', blockRef: 'andring_hyresvillkor.meddelande@v1', part: 'meddelande', input: {} },
        {
            instanceId: 'underskrift-av-meddelandet',
            blockRef: 'andring_hyresvillkor.underskrift@v1',
            part: 'meddelande',
            input: {
                ref: 'underskrift-av-meddelandet',
                title: 'Underskrift av meddelandet',
                parties: MEDDELANDE_SIGNERS,
            },
        },
        {
            instanceId: 'letterhead-tillagg',
            blockRef: 'common.letterhead@v1',
            part: 'tillagg',
            slot: 'letterhead',
            input: { title: TITLE, form_number: FORM_NUMBER },
        },
        {
            instanceId: 'footer-tillagg',
            blockRef: 'common.page-footer@v1',
            part: 'tillagg',
            slot: 'footer',
            input: { form_number: FORM_NUMBER },
        },
        { instanceId: 'tillagg-rubrik', blockRef: 'andring_hyresvillkor.tillagg-rubrik@v1', part: 'tillagg', input: {} },
        { instanceId: 'uppgifter-tillagg', blockRef: 'andring_hyresvillkor.uppgifter@v1', part: 'tillagg', input: {} },
        { instanceId: 'tillagg-ingress', blockRef: 'andring_hyresvillkor.tillagg-ingress@v1', part: 'tillagg', input: {} },
        {
            instanceId: 'andrade-hyresvillkor',
            blockRef: 'andring_hyresvillkor.andrade-hyresvillkor@v1',
            part: 'tillagg',
            input: { numbering: 'auto' },
        },
        {
            instanceId: 'underskrift-av-tillagget',
            blockRef: 'andring_hyresvillkor.underskrift@v1',
            part: 'tillagg',
            input: {
                ref: 'underskrift-av-tillagget',
                title: 'Underskrift',
                numbering: 'auto',
                parties: TILLAGG_SIGNERS,
            },
        },
    ],
});
//# sourceMappingURL=andring_hyresvillkor.js.map